"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./styles/SchoolFeedSplainerPage.module.scss";
import ImageComponent from "../utilities/ImageComponent";
import classNames from "classnames";
import { getDistrict } from "../../utils/SchoolBlocksUtilities";
import ClickableLink from "../utilities/ClickableLink";
import { useStores } from "../../stores/useStores";

function SchoolFeedSplainerPage(props: { lightMode?: boolean }) {
  const backgroundContainerRef = useRef<HTMLDivElement>(null);
  const [backgroundContainerHeight, setBackgroundContainerHeight] = useState(0);
  const { organizationStore, interfaceStore, modalStore } = useStores();
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const isIosSafari =
    /(iPod|iPhone)/.exec(interfaceStore.userAgent) &&
    /Safari/.exec(interfaceStore.userAgent);

  const title1ClassNames = classNames({
    [styles.title1]: true,
    [styles.title1Light]: props.lightMode,
  });

  const text2ClassNames = classNames({
    [styles.text2]: true,
    [styles.text2Light]: props.lightMode,
  });

  useEffect(() => {
    if (backgroundContainerRef.current?.clientHeight) {
      setBackgroundContainerHeight(backgroundContainerRef.current.clientHeight);
    }
  }, [interfaceStore.width, interfaceStore.height]);

  function handleInstallPrompt(e) {
    e.preventDefault();
    setDeferredPrompt(e);
  }

  useEffect(() => {
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
    };
  }, []);

  function getInstall(text, styling?): React.ReactElement | undefined {
    if (organizationStore.organization.type === "district") {
      if (isIosSafari) {
        return (
          <button
            onClick={() =>
              modalStore.addModal({
                type: "iosAppInstallHelp",
              })
            }
            style={styling ? styling : {}}
          >
            {text}
          </button>
        );
      } else {
        return (
          <button
            style={styling ? styling : {}}
            onClick={async () => await deferredPrompt?.prompt()}
          >
            {text}
          </button>
        );
      }
    } else {
      const district = getDistrict(organizationStore.organization);

      return (
        <ClickableLink
          href={
            district.primary_hostname.scheme +
            "://" +
            district.primary_hostname.hostname
          }
        >
          <button style={styling ? styling : {}}>{text}</button>
        </ClickableLink>
      );
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.background} ref={backgroundContainerRef}>
        <div className={styles.backgroundImageTop}>
          <ImageComponent
            alt={""}
            src={require(
              props.lightMode
                ? "../../assets/images/splainerbackgroundlight1.png"
                : "../../assets/images/splainerbackground1.png",
            )}
            className={styles.backgroundImage}
          />
        </div>
        <ImageComponent
          alt={""}
          src={require("../../assets/images/splainerbackground2.png")}
          className={styles.backgroundImage}
          style={{ marginTop: "25%" }}
        />
        <ImageComponent
          alt={""}
          src={require(
            props.lightMode
              ? "../../assets/images/splainerbackgroundlight3.png"
              : "../../assets/images/splainerbackground3.png",
          )}
          className={styles.backgroundImage}
        />
      </div>
      <div
        className={styles.contentContainer}
        style={{ height: backgroundContainerHeight }}
      >
        <div className={styles.topContainer}>
          <div className={styles.textContainer}>
            <div className={title1ClassNames}>
              Stay in the know <br /> with Schoolfeed.
            </div>
            <div className={text2ClassNames}>
              Get the app and customize <br /> your Schoolfeed.
            </div>
            {deferredPrompt && getInstall("Download app")}
          </div>
          <div className={styles.imageContainer}>
            <ImageComponent
              alt={"Image of family"}
              src={require("../../assets/images/splainer1.png")}
              className={styles.image}
            />
          </div>
        </div>
        <div className={styles.subContainer}>
          <div
            className={styles.textContainer}
            style={{ alignItems: "center" }}
          >
            <div className={styles.title3}>Here&#39;s how it works</div>
            <div className={styles.iconList}>
              <div className={styles.iconContainer}>
                <div className={styles.iconImage}>
                  <ImageComponent
                    alt={"Phone with download button"}
                    src={require("../../assets/images/splainer2.png")}
                    className={styles.image}
                  />
                </div>
                {deferredPrompt &&
                  getInstall("Download \nthe app", {
                    backgroundColor: "#485b70",
                  })}
                {!deferredPrompt && (
                  <div className={styles.iconText}>
                    Download <br />
                    the app
                  </div>
                )}
              </div>
              <div className={styles.iconContainer}>
                <div className={styles.iconImage}>
                  <ImageComponent
                    alt={"Computer screens with a pointer cursor and gear"}
                    src={require("../../assets/images/splainer3.png")}
                    className={styles.image}
                  />
                </div>
                <div className={styles.iconText}>
                  Customize <br />
                  your feed
                </div>
              </div>
              <div className={styles.iconContainer}>
                <div className={styles.iconImage}>
                  <ImageComponent
                    alt={"Bell with a check mark"}
                    src={require("../../assets/images/splainer4.png")}
                    className={styles.image}
                  />
                </div>
                <div className={styles.iconText}>
                  Allow <br />
                  notifications
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.subContainer}>
          <div className={styles.imageContainer}>
            <ImageComponent
              alt={"Phone with schoolfeed displayed"}
              src={require("../../assets/images/splainer5.png")}
              className={styles.image}
            />
          </div>
          <div
            className={styles.textContainer}
            style={{ alignItems: "center", justifyContent: "center" }}
          >
            <div className={styles.title2}>
              Be updated on <br />{" "}
              <span style={{ color: "#485b70" }}>all of your devices</span>.
            </div>
            <div className={styles.title2}>
              Get the <span style={{ color: "#485b70" }}>app</span>.
            </div>
            {deferredPrompt && getInstall("Download now")}
          </div>
        </div>
        <div className={styles.subContainer}>
          <div className={styles.textContainer}>
            <div className={styles.title3}>
              Be the <span style={{ color: "#485b70" }}>first to know</span>{" "}
              <br /> what&#39;s new in your <br /> child&#39;s school
              <span style={{ color: "#485b70" }}>.</span>
            </div>
            <div className={styles.text} style={{ color: "#485b70" }}>
              Choose the kind of content you <br /> want to see on your
              child&#39;s feed.
            </div>
            {deferredPrompt && getInstall("Download app")}
          </div>
          <div className={styles.imageContainer} style={{ marginTop: "7%" }}>
            <ImageComponent
              alt={"Schoolfeed interface"}
              src={require("../../assets/images/splainer6.png")}
              className={styles.image}
            />
          </div>
        </div>
        <div className={styles.bottomContainer}>
          <div className={styles.bottomTextContainer}>
            <div className={title1ClassNames}>Customize your feed.</div>
            <div className={text2ClassNames}>
              Find and follow all of the schools, teams <br /> and departments
              that you are interested <br /> in and never miss out on what&#39;s
              new.
            </div>
          </div>
          <div className={styles.bottomImageContainer}>
            {deferredPrompt && (
              <ImageComponent
                alt={"Schoolfeed interface"}
                src={require("../../assets/images/arrow.png")}
                className={styles.arrow}
              />
            )}
            <ImageComponent
              alt={"Schoolfeed interface"}
              src={require("../../assets/images/splainer7.png")}
              className={styles.bottomImage}
            />
            <div className={styles.bottomButton}>
              {deferredPrompt && getInstall("Customize my Schoolfeed")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SchoolFeedSplainerPage;
